<template>
  <div class="signup-page" :style="{ backgroundImage: `url(${designUrl}${isMobile ? 'LandingPage_phone.png' : 'LandingPage.png'})` }">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">
          <li @click="goToPage('SignupPage')">Sign Up</li>
        </ul>
      </div>
    </div>

    <div class="page-header">
      <h1 class="page-title">Sign Up</h1>
    </div>

    <div class="page-wrapper">
      <div class="form-card">
        <form @submit.prevent="submitForm">

          <div class="form-group">
            <label class="form-label">Full Name</label>
            <input class="form-input" v-model="form.fullname" type="text" placeholder="Full Name" required />
          </div>

          <div class="form-group">
            <label class="form-label">Phone Number</label>
            <input
              class="form-input"
              v-model="form.phone_number"
              type="text"
              placeholder="10-digit number"
              maxlength="10"
              @keypress="onPhoneKeypress"
              @input="onPhoneInput"
            />
            <p v-if="phoneError" class="phone-error">{{ phoneError }}</p>
          </div>

          <div class="form-group">
            <label class="form-label">Email</label>
            <input class="form-input" v-model="form.email" type="email" placeholder="Email" required />
          </div>

          <div class="form-group">
            <label class="form-label">Location</label>
            <div style="margin-top: 10px;">
              <button class="location-btn" type="button" @click="detectLocation">
                📍 Use My Location
              </button>
              <p v-if="locationError" class="phone-error">{{ locationError }}</p>
            </div>
            <input
              class="form-input"
              v-model="manualPlace"
              type="text"
              placeholder="Or enter city, place, or address"
              style="margin-top: 10px;"
              :disabled="!autoLocation"
            />
          </div>

          <div class="form-group">
            <label class="form-label">User Type</label>
            <div class="pill-group">
              <button
                class="pill-option"
                type="button"
                :class="{ 'pill-active': form.user_type === 'Pet Owner' }"
                @click="form.user_type = 'Pet Owner'"
              >Pet Owner</button>
              <button
                class="pill-option"
                type="button"
                :class="{ 'pill-active': form.user_type === 'Pet Lover' }"
                @click="form.user_type = 'Pet Lover'"
              >Pet Lover</button>
            </div>
          </div>
          <p v-if="formError" class="phone-error">{{ formError }}</p>
          <button class="submit-btn" type="submit">Register</button>

        </form>

        <div v-if="showPetPrompt" class="popup-overlay">
          <div class="popup-card">
            <div class="popup-icon">🐾</div>
            <h2 class="popup-title">Add Your Pet?</h2>
            <p class="popup-sub">Would you like to add your pet's profile now so others can find them?</p>
            <div class="popup-btns">
              <button class="popup-btn popup-yes" @click="goToAddPet">Yes, let's go!</button>
              <button class="popup-btn popup-later" @click="goToDashboard">Do it later</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
const backendUrl = process.env.VUE_APP_API_URL;

export default {
  name: 'SignupPage',
  props: {
    initialFullname: { type: String, default: '' },
    initialEmail: { type: String, default: '' },
    user: {
      type: Object,
      default: () => ({ fullname: '', phone_number: '' })
    }
  },
  emits: ['user-registered', 'change-page'],
  data() {
    return {
      designUrl: '/background_designs/',
      isMobile: window.innerWidth <= 480,
      form: {
        fullname: this.initialFullname,
        phone_number: '',
        email: this.initialEmail,
        user_type: '',
        location: '',
        location_coords: ''
      },
      phoneError: '',
      manualPlace: '',
      autoLocation: null,
      sidebarOpen: false,
      showPetPrompt: false,
      formError: '',
      locationError: '',
    };
  },
  watch: {
    initialEmail(val) { this.form.email = val; },
    initialFullname(val) { this.form.fullname = val; }
  },
  methods: {
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    onPhoneKeypress(e) {
      if (!/\d/.test(e.key)) {
        e.preventDefault();
        this.phoneError = 'Only numbers are allowed';
      }
    },
    onPhoneInput() {
      this.form.phone_number = this.form.phone_number.replace(/\D/g, '').slice(0, 10);
      if (this.form.phone_number.length === 0) {
        this.phoneError = '';
      } else if (this.form.phone_number.length < 10) {
        this.phoneError = `${10 - this.form.phone_number.length} more digit(s) needed`;
      } else {
        this.phoneError = '';
      }
    },
    async detectLocation() {
      if (!navigator.geolocation) {
        this.locationError = 'Geolocation not supported on this device';
        return;
      }
      navigator.geolocation.getCurrentPosition(
        async pos => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          this.form.location_coords = `${lat},${lon}`;

          // reverse geocode to get human-readable address
          try {
            const resp = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`
            );
            const data = await resp.json();
            const address = data.display_name || `${lat},${lon}`;
            this.form.location = address;
            this.locationDisplay = address;  // for EditUserDashboard
            this.manualPlace = address;      // for SignupPage
          } catch {
            this.form.location = `${lat},${lon}`;
            this.locationDisplay = `${lat},${lon}`;
            this.manualPlace = `${lat},${lon}`;
          }

          this.locationDetected = true;
          this.autoLocation = { lat, lon };
          this.locationError = '';
        },
        err => {
          this.locationError = 'Could not detect location. Try entering manually.';
          console.error('Location error:', err);
        }
      );
    },
    async getCoordsFromPlace(place) {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(place)}&format=json&limit=1`
      );
      const results = await resp.json();
      if (!results.length) throw new Error('Place not found');
      const { lat, lon } = results[0];
      return { lat, lon };
    },
    async submitForm() {
      this.formError = '';
      if (!this.form.fullname.trim() || !this.form.email.trim()) {
        this.formError = 'Please fill in your name and email'; return;
      }
      if (!this.form.user_type) {
        this.formError = 'Please select Pet Owner or Pet Lover'; return;
      }
      if (this.form.phone_number.length !== 10) {
        this.phoneError = 'Phone number must be exactly 10 digits';
        return;
      }
      this.phoneError = '';

      if (this.manualPlace.trim() && !this.autoLocation) {
        try {
          const { lat, lon } = await this.getCoordsFromPlace(this.manualPlace);
          this.form.location_coords = `${lat},${lon}`;
          this.form.location = this.manualPlace;
        } catch {
          this.formError = 'Please enter a valid location'; return;
        }
      } else if (!this.manualPlace.trim() && !this.autoLocation) {
        this.formError = 'Please enter a location or use detect location'; return;
      }

      try {
        const { data } = await axios.post(
          `${backendUrl}/api/auth/signup`,
          this.form,
          { withCredentials: true }
        );
        this.$emit('user-registered', data.user);
        if (this.form.user_type === 'Pet Owner') {
          this.showPetPrompt = true;
        } else {
          this.$emit('change-page', 'DashboardPage');
        }
      } catch (err) {
        console.error('Signup error:', err.response?.data || err);
        this.formError = err.response?.data?.message || 'Signup failed. Please try again.';
      }
    },
    handleResize() {
          this.isMobile = window.innerWidth <= 480;
        },
        goToAddPet() {
      this.showPetPrompt = false;
      this.$emit('change-page', 'AddPetPage');
    },
    goToDashboard() {
      this.showPetPrompt = false;
      this.$emit('change-page', 'DashboardPage');
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}

.signup-page {
  min-height: 100vh;
  width: 100%;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-attachment: scroll;
  background-color: #f5ece0;
}

.page-header {
  max-width: 1200px;
  margin: 0 auto;
  padding: 28px 20px 0 24px;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
  margin: 0;
  margin-top: 8px; margin-left: 7px;
}

.page-sub {
  font-size: 13px;
  color: #aaa;
  font-weight: 500;
  margin-top: 2px;
}

.page-wrapper {
  max-width: 960px;
  margin: 0 auto;
  padding: 20px 24px 40px;
}

.popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.popup-card {
  background: #fff;
  border-radius: 24px;
  border: 1px solid #f0e0ff;
  padding: 36px 28px;
  max-width: 360px;
  width: 100%;
  text-align: center;
  box-shadow: 0 8px 32px rgba(186, 113, 255, 0.15);
}

.popup-icon { font-size: 40px; margin-bottom: 12px; }

.popup-title {
  font-size: 20px;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.popup-sub {
  font-size: 13px;
  color: #aaa;
  font-weight: 500;
  line-height: 1.6;
  margin-bottom: 24px;
}

.popup-btns {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.popup-btn {
  width: 100%;
  padding: 11px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.18s, transform 0.1s;
  font-family: inherit;
}

.popup-yes {
  background: #eac7ff;
  color: #4a1070;
}
.popup-yes:hover { background: #ba71ff; color: #fff; }

.popup-later {
  background: #f5f5f5;
  color: #888;
  border: 1.5px solid #e8e8e8;
}
.popup-later:hover { background: #efefef; }

.form-card {
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid #f0e0ff;
  padding: 34px 32px;
}

form {
  width: 100%;
}

.form-group {
  margin-bottom: 14px;
}

.form-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 5px;
}

.form-input:disabled {
  background: #f0f0f0;
  color: #bbb;
  cursor: not-allowed;
  border-color: #e0e0e0;
}

.form-input {
  width: 100%;
  padding: 9px 13px;
  border: 1.5px solid #f0e0ff;
  border-radius: 11px;
  font-size: 14px;
  background: #fffbe8;
  outline: none;
  transition: all 0.2s ease;
  color: #1a1a1a;
  font-family: inherit;
}

.form-input:focus {
  border-color: #ba71ff;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(186, 113, 255, 0.15);
}

.phone-error {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #c0392b;
  background: #fde8e8;
  border-radius: 8px;
  padding: 6px 10px;
}

.location-btn {
  width: 100%;
  padding: 9px 13px;
  border: 1.5px solid #f0e0ff;
  border-radius: 11px;
  font-size: 14px;
  font-weight: 700;
  background: #fffbe8;
  color: #4a1070;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  font-family: inherit;
}

.location-btn:hover {
  background: #eac7ff;
  border-color: #ba71ff;
}

.pill-group {
  display: flex;
  gap: 8px;
}

.pill-option {
  flex: 1;
  padding: 9px 13px;
  border: 1.5px solid #f0e0ff;
  border-radius: 11px;
  font-size: 14px;
  font-weight: 700;
  background: #fffbe8;
  color: #888;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pill-option:hover {
  background: #eac7ff;
  border-color: #ba71ff;
}

.pill-active {
  background: #eac7ff;
  border-color: #ba71ff;
  color: #4a1070;
}

.submit-btn {
  width: 100%;
  padding: 11px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.18s, transform 0.1s;
  letter-spacing: 0.01em;
  margin-top: 10px;
  background: #eac7ff;
  color: #4a1070;
  font-family: inherit;
}

.submit-btn:hover {
  background: #ba71ff;
  color: #fff;
}

.submit-btn:active {
  transform: scale(0.98);
}

/* Desktop grid */
@media (min-width: 600px) {
  form {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 28px;
  }

  .form-group:has(.location-btn),
  .form-group:last-of-type,
  .submit-btn {
    grid-column: 1 / -1;
  }

  .form-input {
    padding: 11px 15px;
    font-size: 15px;
    border-radius: 13px;
  }

  .location-btn,
  .pill-option {
    padding: 11px 15px;
    font-size: 15px;
    border-radius: 13px;
  }

  .form-label {
    font-size: 12px;
    margin-bottom: 6px;
  }

  .form-group {
    margin-bottom: 56px;
  }

  .submit-btn {
    padding: 13px;
    font-size: 15px;
  }
}

/* Mobile */
@media (max-width: 480px) {
  .page-header {
    padding: 20px 16px 0 46px;
  }

  .page-wrapper {
    padding: 12px 12px 16px;
  }

  .form-card {
    padding: 10px 12px;
  }

  .form-group {
    margin-bottom: 8px;
  }

  .form-label {
    font-size: 9px;
    margin-bottom: 3px;
  }

  .form-input {
    padding: 6px 9px;
    font-size: 11px;
    border-radius: 8px;
  }

  .location-btn,
  .pill-option {
    padding: 6px 9px;
    font-size: 11px;
    border-radius: 8px;
  }

  .submit-btn {
    padding: 8px;
    font-size: 11px;
    margin-top: 6px;
  }

  .phone-error {
    font-size: 11px;
    padding: 6px 9px;
  }
}
</style>